import React from 'react';
import { User } from '@/types';
import { DEFAULT_BRAND_COLOR, HEX_RE } from './_constants';

// Branding card — logo upload + brand color picker + cover banner preview.
// Stateless; all state lives in Settings.tsx and is passed in via props.

interface BrandingCardProps {
  profile: User | null;
  brandColor: string;
  companyName: string;
  fileInputRef: React.RefObject<HTMLInputElement>;
  uploadingLogo: boolean;
  resettingColor: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLogoFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveLogo: () => void;
  onResetBrandColor: () => void;
}

export const BrandingCard: React.FC<BrandingCardProps> = ({
  profile,
  brandColor,
  companyName,
  fileInputRef,
  uploadingLogo,
  resettingColor,
  onChange,
  onLogoFile,
  onRemoveLogo,
  onResetBrandColor,
}) => {
  const previewColor = HEX_RE.test(brandColor) ? brandColor : DEFAULT_BRAND_COLOR;
  const previewLabel = companyName || 'Your Company';

  return (
    <div className="card">
      <h2 className="text-heading-md text-ink mb-1">Report Branding</h2>
      <p className="text-body-sm text-ink-subtle mb-4">
        When you set a logo or brand color, Flowspect branding is replaced throughout your PDF reports.
      </p>

      <div className="space-y-6">
        {/* Logo */}
        <div>
          <label className="block text-body-sm font-medium text-ink-muted mb-2">Company Logo</label>
          <div className="flex items-center gap-4">
            <div className="w-40 h-20 rounded-md border border-border bg-surface-2 flex items-center justify-center overflow-hidden">
              {profile?.logoUrl ? (
                <img
                  src={profile.logoUrl}
                  alt="Logo preview"
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <span className="text-caption text-ink-faint">No logo</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={onLogoFile}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                className="btn-secondary"
              >
                {uploadingLogo ? 'Uploading…' : profile?.logoUrl ? 'Replace Logo' : 'Upload Logo'}
              </button>
              {profile?.logoUrl && (
                <button
                  type="button"
                  onClick={onRemoveLogo}
                  disabled={uploadingLogo}
                  className="px-4 py-2 text-body-sm font-medium rounded-md border border-sev-major/30 bg-surface-1 text-sev-major hover:bg-sev-major-soft disabled:opacity-50 transition-colors"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
          <p className="mt-2 text-caption text-ink-subtle">PNG, JPG, WebP, or SVG. Up to 5 MB. Renders up to 44px tall on the report cover.</p>
        </div>

        {/* Brand color */}
        <div>
          <label className="block text-body-sm font-medium text-ink-muted mb-2">Brand Color</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              name="brandColor"
              value={brandColor}
              onChange={onChange}
              className="h-10 w-20 rounded cursor-pointer border border-border"
            />
            <input
              type="text"
              name="brandColor"
              value={brandColor}
              onChange={onChange}
              placeholder={DEFAULT_BRAND_COLOR}
              className="input-field w-32 font-mono text-body-sm"
            />
            <button
              type="button"
              onClick={onResetBrandColor}
              disabled={resettingColor}
              className="text-caption text-ink-subtle hover:text-ink underline disabled:opacity-50"
            >
              {resettingColor ? 'Resetting…' : 'Reset to default'}
            </button>
          </div>
        </div>

        {/* Cover banner preview */}
        <div>
          <div className="text-body-sm font-medium text-ink-muted mb-2">Cover Banner Preview</div>
          <div
            className="rounded-md overflow-hidden border border-border flex items-center px-6"
            style={{ backgroundColor: previewColor, minHeight: '78px' }}
          >
            {profile?.logoUrl ? (
              <img
                src={profile.logoUrl}
                alt="Logo"
                className="max-h-11 max-w-[260px] object-contain"
              />
            ) : (
              <span className="text-white font-bold text-xl tracking-wide">
                {companyName ? previewLabel : '◆ FLOWSPECT'}
              </span>
            )}
          </div>
          <p className="mt-2 text-caption text-ink-subtle">
            This is how the top of every report PDF will appear.
          </p>
        </div>
      </div>
    </div>
  );
};
